import { useCallback } from 'react';

import { DEFAULT_PRO_SUBSCRIBE_OFFER_CODE } from '@components/pages/NewsletterIndexLanding/NewsletterCard/interfaces';
import { SUBSCRIBE_NOW, UPGRADE_TO_PRO } from '@components/pages/OrgCharts/constants';
import { subscribe_one_path, upgrade_subscription_path } from '@routes';
import { ClickTarget, EventProperties, click } from '@tracking';
import { User } from '@utils/backend-api/currentUser';

interface UsePaywallCTAsProps {
  currentUser: User;
  offer?: string;
  subscribeText?: string;
  trackEvent: (event: EventProperties) => void;
  upgradeText?: string;
}

interface UsePaywallCTAsReturnValues {
  handleCTAClickTracking: () => void;
  paywallCTAPath: () => string | undefined;
  paywallCTAText: string;
}

const usePaywallCTAs = ({
  currentUser,
  offer,
  subscribeText = SUBSCRIBE_NOW,
  upgradeText = UPGRADE_TO_PRO,
  trackEvent,
}: UsePaywallCTAsProps): UsePaywallCTAsReturnValues => {
  const isSubscriber: boolean = currentUser?.is_subscriber;
  const isLead: boolean = currentUser && !isSubscriber;
  const isCBPT: boolean = currentUser?.subscription?.is_cbpt;
  const showUpgradeCTA: boolean = isSubscriber && !isCBPT;
  const showKnownUserSubscribeCTA: boolean = isLead || isCBPT;
  const paywallCTAText: string = showUpgradeCTA
    ? upgradeText
    : showKnownUserSubscribeCTA
    ? subscribeText
    : '';

  const paywallCTAPath = useCallback(() => {
    if (showUpgradeCTA) {
      return offer ? upgrade_subscription_path({ offer }) : upgrade_subscription_path();
    } else if (showKnownUserSubscribeCTA) {
      const offerCode = offer || DEFAULT_PRO_SUBSCRIBE_OFFER_CODE;
      return subscribe_one_path({ offer: offerCode });
    }
  }, [showUpgradeCTA, showKnownUserSubscribeCTA, offer]);

  const handleCTAClickTracking = useCallback(() => {
    if (showUpgradeCTA) {
      trackEvent(click({ clickTarget: ClickTarget.Upgrade, clickText: upgradeText }));
    } else if (showKnownUserSubscribeCTA) {
      trackEvent(click({ clickTarget: ClickTarget.Subscribe, clickText: subscribeText }));
    }
  }, [showUpgradeCTA, showKnownUserSubscribeCTA, subscribeText, trackEvent, upgradeText]);

  return {
    handleCTAClickTracking,
    paywallCTAPath,
    paywallCTAText,
  };
};

export default usePaywallCTAs;
